import {
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  UserOutlined,
} from '@ant-design/icons';
import AlarmNotification from '@components/AlarmNotification/AlarmNotification';
import LanguageSelector from '@components/LanguageSelector';
import Logo from '@components/Logo';
import SelectContext from '@components/SelectContext';
import SwitchTheme from '@components/SwitchTheme';
import KeycloakContext from '@providers/Keycloak/KeycloakContext';
import SessionContext from '@providers/Session/SessionContext';
import { ApplicationsConstants } from '@utils/applicationsConstants';
import { Button, Divider, Popover, Tooltip } from 'antd';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { VscBook } from 'react-icons/vsc';

import { NotificationItem, useNotifications } from '@hooks/useNotifications';
import {
  AlarmNotificationContent,
  AlarmData,
} from '@components/AlarmNotificationContent/AlarmNotificationContent';
import { AlarmNotificationActionButtons } from '@components/AlarmNotificationActionButtons/AlarmNotificationActionButtons';
import {
  List as VirtuList,
  CellMeasurer,
  CellMeasurerCache,
} from 'react-virtualized';
import * as S from './styles';

interface PagesHeaderProps {
  showPagesMenu: boolean;
  togglePagesMenu: () => void;
}

const GAP = 8;
const POPOVER_WIDTH = 560;
const LIST_HEIGHT = 364;

const cache = new CellMeasurerCache({
  fixedWidth: true,
  defaultHeight: 110,
  minHeight: 90,
});

const PagesHeader = ({ showPagesMenu, togglePagesMenu }: PagesHeaderProps) => {
  const { t } = useTranslation(['main', 'alarms-notifications']);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isPageHome = pathname === '/';
  const { keycloak } = useContext(KeycloakContext);
  const notificationContentRef = useRef<HTMLDivElement>(null);
  const [hasScrollbar, setHasScrollbar] = useState(false);
  const [notificationPopoverOpen, setNotificationPopoverOpen] = useState(false);
  const [listKey, setListKey] = useState(0);
  const [isContentReady, setIsContentReady] = useState(false);
  const { theme, isNotChildOfRoot, isAlgarDomain } = useContext(SessionContext);

  const {
    filteredNotifications,
    isNotificationsLoading,
    loadingNotifications,
    handleRemoveNotification,
    handleNotificationClick,
    handleNavigateToAlarmConfigPage,
    handleNavigateToAlarmPage,
    formatAlarmType,
  } = useNotifications();

  useEffect(() => {
    if (notificationPopoverOpen) {
      const timeout = setTimeout(() => {
        const el = notificationContentRef.current;
        if (el) setHasScrollbar(el.scrollHeight > el.clientHeight);
      }, 0);
      return () => clearTimeout(timeout);
    }
    return undefined;
  }, [notificationPopoverOpen, theme.title]);

  useEffect(() => {
    cache.clearAll();
  }, [filteredNotifications, theme.title]);

  useEffect(() => {
    if (notificationPopoverOpen) {
      cache.clearAll();
    }
  }, [notificationPopoverOpen]);

  const menuContent = useMemo(
    () => (
      <S.MenuContent>
        <Button
          type="link"
          icon={<UserOutlined />}
          onClick={() => navigate(`${ApplicationsConstants.ACCESS}/my-user`)}
          style={{ textAlign: 'left', padding: 0 }}
        >
          {t('my-user')}
        </Button>
        <LanguageSelector className="header-language-selector" />
        <SwitchTheme className="header-switch-theme" />
        <Divider />
        <Button
          type="link"
          icon={<LogoutOutlined />}
          onClick={() => keycloak?.logout()}
          style={{ textAlign: 'left', padding: 0 }}
        >
          {t('logout')}
        </Button>
      </S.MenuContent>
    ),
    [keycloak, navigate, t],
  );

  const NotificationRowItem = React.memo(
    (props: {
      alarmData: AlarmData;
      isRemoving: boolean;
      onNavigate: () => void;
      onRemove: () => Promise<void> | void;
    }) => {
      const { alarmData, isRemoving, onNavigate, onRemove } = props;
      return (
        <div style={{ paddingRight: 8 }}>
          <AlarmNotificationContent
            alarmData={alarmData}
            isToast={false}
            navigateToAlarmPage={onNavigate}
            onRemove={onRemove}
            isRemoving={isRemoving}
            showRemoveButton
          />
        </div>
      );
    },
  );

  const notificationContent = useMemo(() => {
    const getTipoDisparo = (disparo: string) => {
      if (disparo === 'CRITICAL') return 'critical';
      if (disparo === 'OK') return 'ok';
      if (disparo === 'CRIT') return 'crit';
      return 'recovery';
    };

    const listItems = filteredNotifications.map(
      (notification: NotificationItem, index: number) => ({
        label: notification.nomeAlarme,
        value: index,
        alarmData: {
          ...notification,
          tipoAlarme: formatAlarmType(
            notification.tipoAlarme,
            notification.tipoAlarmeOriginal,
          ),
          tipoDisparo: getTipoDisparo(notification.disparo),
        } as AlarmData,
        isRemoving: loadingNotifications.has(notification.idAlarme),
        onNavigate: () =>
          handleNotificationClick(notification, () =>
            setNotificationPopoverOpen(false),
          ),
        onRemove: async () => {
          await handleRemoveNotification(
            notification.idAlarme,
            notification.timeId,
          );
        },
      }),
    );

    return (
      <S.NotificationContent
        ref={notificationContentRef}
        className={hasScrollbar ? 'has-scrollbar' : ''}
        style={{
          width: POPOVER_WIDTH,
          height: filteredNotifications.length > 0 ? 420 : 60,
          padding: 0,
          overflow: 'visible',
        }}
      >
        {filteredNotifications.length > 0 ? (
          <>
            <div style={{ width: '100%' }}>
              <AlarmNotificationActionButtons
                isEmpty={false}
                navigateToAlarmConfigPage={() => {
                  handleNavigateToAlarmConfigPage();
                  setNotificationPopoverOpen(false);
                }}
                navigateToAlarmPage={() => {
                  handleNavigateToAlarmPage();
                  setNotificationPopoverOpen(false);
                }}
              />
              <div
                style={{
                  height: LIST_HEIGHT,
                  width: '100%',
                  paddingBottom: GAP,
                  boxSizing: 'border-box',
                }}
              >
                {isContentReady && (
                  /* @ts-ignore */
                  <VirtuList
                    key={`desktop-list-${listKey}`}
                    height={LIST_HEIGHT}
                    width={POPOVER_WIDTH}
                    rowCount={listItems.length}
                    deferredMeasurementCache={cache}
                    rowHeight={cache.rowHeight}
                    overscanRowCount={4}
                    style={{ outline: 'none' }}
                    noRowsRenderer={() => (
                      <div style={{ padding: 16 }}>Sem itens</div>
                    )}
                    rowRenderer={({ key, index, style, parent }) => {
                      const item = listItems[index];
                      if (!item) return null;

                      return (
                        // @ts-ignore
                        <CellMeasurer
                          key={key}
                          cache={cache}
                          parent={parent}
                          columnIndex={0}
                          rowIndex={index}
                        >
                          {({ registerChild }) => (
                            <div
                              ref={registerChild as React.Ref<HTMLDivElement>}
                              style={{
                                ...style,
                                paddingBottom: GAP,
                                boxSizing: 'border-box',
                              }}
                              className="virtual-list-item"
                            >
                              <NotificationRowItem {...item} />
                            </div>
                          )}
                        </CellMeasurer>
                      );
                    }}
                  />
                )}
              </div>
            </div>
          </>
        ) : (
          <AlarmNotificationActionButtons
            isEmpty
            navigateToAlarmConfigPage={() => {
              handleNavigateToAlarmConfigPage();
              setNotificationPopoverOpen(false);
            }}
            navigateToAlarmPage={() => {
              handleNavigateToAlarmPage();
              setNotificationPopoverOpen(false);
            }}
          />
        )}
      </S.NotificationContent>
    );
  }, [
    filteredNotifications,
    hasScrollbar,
    handleNavigateToAlarmConfigPage,
    handleNavigateToAlarmPage,
    NotificationRowItem,
    formatAlarmType,
    loadingNotifications,
    handleNotificationClick,
    handleRemoveNotification,
    listKey,
    isContentReady,
  ]);

  return (
    <S.HeaderContainer isPageHome={isPageHome}>
      <div className="header-menu-logo">
        {!isPageHome && (
          <Tooltip title={t(`${showPagesMenu ? 'hide' : 'display'}Menu`)}>
            <Button
              icon={
                showPagesMenu ? <MenuFoldOutlined /> : <MenuUnfoldOutlined />
              }
              onClick={togglePagesMenu}
              style={{ marginRight: 16 }}
            />
          </Tooltip>
        )}
        <Tooltip title={t(`${showPagesMenu ? 'hide' : 'display'}Menu`)}>
          <Logo />
        </Tooltip>
      </div>
      <div className="header-right-items main-header-desktop hide-on-mobile">
        <SelectContext width="320px" />
        <Popover
          trigger="click"
          content={notificationContent}
          placement="bottomRight"
          open={notificationPopoverOpen}
          destroyTooltipOnHide
          onOpenChange={open => {
            setNotificationPopoverOpen(open);
            if (open) {
              setIsContentReady(false);
              cache.clearAll();
              setListKey(k => k + 1);
              requestAnimationFrame(() => {
                setIsContentReady(true);
                requestAnimationFrame(() => {
                  const el = notificationContentRef.current;
                  if (el) setHasScrollbar(el.scrollHeight > el.clientHeight);
                });
              });
            } else {
              setIsContentReady(false);
            }
          }}
        >
          <div style={{ cursor: 'pointer' }}>
            <AlarmNotification
              count={filteredNotifications.length}
              isLoading={isNotificationsLoading}
            />
          </div>
        </Popover>
        {isAlgarDomain && !isNotChildOfRoot && (
          <Tooltip title={t('menu.support.user-manual')}>
            <Button
              icon={
                <VscBook style={{ marginTop: 5, transform: 'scale(1.4)' }} />
              }
              style={{ marginLeft: 8 }}
              onClick={() => {
                let domain = new URL(window.location.href).hostname;
                if (
                  domain === 'localhost' &&
                  process.env.REACT_APP_HML_HOSTNAME
                ) {
                  domain = process.env.REACT_APP_HML_HOSTNAME;
                }
                const userManualPage = `https://docs.${domain}/getting-started`;
                window.open(userManualPage, '_blank');
              }}
            />
          </Tooltip>
        )}
        <Popover trigger="click" content={menuContent} placement="bottomRight">
          <Button icon={<UserOutlined />} style={{ marginLeft: 8 }} />
        </Popover>
      </div>
    </S.HeaderContainer>
  );
};

export default PagesHeader;
